/**
 * Navbar Secura Web — même style que NavbarAdminRH (compte à droite).
 */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes, FaSignOutAlt } from 'react-icons/fa';
import { Bell, BellOff, Laptop, LogOut, Monitor, Search, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { AppContext } from '../../contexts/AppContext';
import { assets } from '../../assets/assets';
import toast from 'react-hot-toast';
import axiosInstance from '../../config/axios';
import { getLogoUrl, getPhotoUrl } from '../../utils/imageUtils';
import useWebNotifications from '../../hooks/useWebNotifications';
import NotificationModal from '../employee/NotificationModal';
import { apiGet } from '../../utils/api';

const currentDeviceId = (token) => {
  try {
    const payload = JSON.parse(atob(String(token).split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload?.lds ? Number(payload.lds) : null;
  } catch {
    return null;
  }
};

const formatDate = (d) =>
  d ? new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

function Dialog({ title, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return createPortal(
    <div className="fixed inset-0 z-[10000] bg-black/30 flex items-center justify-center p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={title} className="w-full max-w-md bg-white rounded-2xl shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <h2 className="text-base font-bold text-gray-900">{title}</h2>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center" aria-label="Fermer">
            <X size={18} className="text-gray-500" />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}

function LinkedDevicesDialog({ token, onClose, onRevokedSelf }) {
  const [devices, setDevices] = useState(null);
  const [busy, setBusy] = useState(null);
  const selfId = currentDeviceId(token);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosInstance.get('/auth/linked-devices');
        setDevices(Array.isArray(data?.devices) ? data.devices : []);
      } catch {
        setDevices([]);
        toast.error('Impossible de charger les appareils');
      }
    })();
  }, []);

  const revoke = async (device) => {
    setBusy(device.id);
    try {
      await axiosInstance.delete(`/auth/linked-devices/${device.id}`);
      if (Number(device.id) === selfId) {
        onRevokedSelf();
        return;
      }
      setDevices((list) => list.filter((d) => d.id !== device.id));
      toast.success('Appareil déconnecté');
    } catch {
      toast.error('Échec de la déconnexion de cet appareil');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog title="Appareils connectés" onClose={onClose}>
      <div className="px-5 pb-5">
        <p className="text-sm text-gray-500 mb-3">Ordinateurs ouverts avec le QR code de l’app Secura.</p>
        {devices === null ? (
          <div className="h-24 rounded-xl bg-gray-100 animate-pulse" />
        ) : !devices.length ? (
          <p className="text-sm text-gray-500 py-6 text-center">Aucun appareil connecté.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {devices.map((d) => {
              const self = Number(d.id) === selfId;
              return (
                <li key={d.id} className="py-3 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
                    {d.mode === 'personal' ? <Laptop size={18} className="text-gray-600" /> : <Monitor size={18} className="text-gray-600" />}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-gray-900 truncate">
                      {d.label || 'Ordinateur'}
                      {self ? <span className="ml-2 text-xs font-medium text-[#0026E2]">Cet ordinateur</span> : null}
                    </span>
                    <span className="block text-xs text-gray-500">Actif le {formatDate(d.last_seen_at || d.created_at)}</span>
                  </span>
                  <button type="button" disabled={busy === d.id} onClick={() => revoke(d)} className="min-h-[36px] px-3 text-sm font-semibold text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50">
                    Déconnecter
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Dialog>
  );
}

export default function NavbarEmployeeWeb({ onMenuClick, isCollapsed = false }) {
  const location = useLocation();
  const onProjets = location.pathname.startsWith('/projets');
  const { user, token, handleLogout } = useAuth();
  const { userData } = useContext(AppContext);
  const { permission, request } = useWebNotifications();
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [dialog, setDialog] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [logoNonce, setLogoNonce] = useState(Date.now());
  const [avatarFailed, setAvatarFailed] = useState(false);
  const accountMenuRef = useRef(null);
  const [projectSearch, setProjectSearch] = useState('');

  useEffect(() => {
    if (!onProjets) {
      setProjectSearch('');
      window.dispatchEvent(new CustomEvent('secura-web-project-search', { detail: '' }));
      return;
    }
    window.dispatchEvent(new CustomEvent('secura-web-project-search', { detail: projectSearch }));
  }, [projectSearch, onProjets]);

  const displayName = user?.nom_complet || userData?.nom_complet || 'Employé';
  const firstName = useMemo(() => displayName.trim().split(/\s+/).filter(Boolean)[0] || displayName, [displayName]);
  const profileSrc = useMemo(() => {
    const raw = userData?.profileImageUrl || userData?.photo || user?.profileImageUrl || user?.photo || null;
    if (!raw || avatarFailed) return null;
    return getPhotoUrl(raw);
  }, [userData?.profileImageUrl, userData?.photo, user?.profileImageUrl, user?.photo, avatarFailed]);

  const companyLogoSrc = useMemo(() => {
    if (!companyLogo) return null;
    return `${getLogoUrl(companyLogo)}?v=${logoNonce}`;
  }, [companyLogo, logoNonce]);

  const renderProfileAvatar = (sizeClass) =>
    profileSrc ? (
      <img
        src={profileSrc}
        alt=""
        className={`${sizeClass} rounded-full object-cover flex-shrink-0`}
        onError={() => setAvatarFailed(true)}
      />
    ) : (
      <img src={assets.employes} alt="" className={`${sizeClass} object-contain flex-shrink-0`} />
    );

  // Logo entreprise : depuis /user/data (accessible employé), pas /entreprise/settings (AdminRH only)
  useEffect(() => {
    const fromUserData =
      userData?.logoEntreprise ||
      userData?.entreprise?.logo_rapport ||
      userData?.entreprise?.logo ||
      null;
    const nameFromUserData = userData?.company || userData?.entreprise?.nom || '';
    if (fromUserData) {
      setCompanyLogo(fromUserData);
      setLogoNonce(Date.now());
    }
    if (nameFromUserData) setCompanyName(nameFromUserData);
  }, [userData?.logoEntreprise, userData?.entreprise?.logo_rapport, userData?.entreprise?.logo, userData?.company, userData?.entreprise?.nom]);

  useEffect(() => {
    if (companyLogo) return undefined;
    let mounted = true;
    (async () => {
      try {
        const { data } = await axiosInstance.get('/auth/me');
        if (!mounted || !data?.success) return;
        const logo = data.user?.entreprise?.logo_rapport || data.user?.entreprise?.logo || null;
        if (logo) {
          setCompanyLogo(logo);
          setLogoNonce(Date.now());
        }
        if (data.user?.entreprise?.nom) setCompanyName(data.user.entreprise.nom);
      } catch {
        // silencieux
      }
    })();
    return () => {
      mounted = false;
    };
  }, [companyLogo, user?.entreprise_id]);

  useEffect(() => {
    if (!showAccountMenu) return undefined;
    const onMouseDown = (e) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(e.target)) setShowAccountMenu(false);
    };
    window.addEventListener('mousedown', onMouseDown);
    return () => window.removeEventListener('mousedown', onMouseDown);
  }, [showAccountMenu]);

  useEffect(() => {
    if (!user) return undefined;
    let mounted = true;
    const loadUnread = async () => {
      try {
        const response = await apiGet('/api/notifications/count', { silent: true });
        if (mounted && response?.success) setUnreadCount(response.count ?? response.unreadCount ?? 0);
      } catch {
        // silencieux
      }
    };
    loadUnread();
    const interval = setInterval(loadUnread, 30000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [user, isNotificationModalOpen]);

  const notif =
    permission === 'granted'
      ? { icon: Bell, label: 'Notifications activées', hint: 'Bandeau et son à chaque message', action: null }
      : permission === 'denied'
        ? { icon: BellOff, label: 'Notifications bloquées', hint: 'À autoriser dans les réglages du navigateur', action: null }
        : permission === 'unsupported'
          ? null
          : { icon: Bell, label: 'Activer les notifications', hint: 'Bandeau et son à chaque message', action: request };

  return (
    <header className="shadow-sm border-b sticky top-0 z-30 backdrop-blur-sm bg-white/95 border-gray-200">
      <div className="flex items-center justify-between h-16 px-6">
        <button type="button" onClick={onMenuClick} className="lg:hidden p-2 rounded-lg hover:bg-gray-100" aria-label="Menu">
          <img src={assets.secura_logo} alt="Secura" className="h-8 w-auto object-contain" />
        </button>
        <div className="hidden lg:flex items-center gap-3 flex-1 min-w-0 max-w-2xl">
          {/* Même taille que le logo sidebar ouverte (h-32 w-44) */}
          {isCollapsed ? (
            <img
              src={assets.secura_logo}
              alt="Secura"
              className="h-32 w-44 object-contain shrink-0 -ml-6"
            />
          ) : null}
          {onProjets ? (
            <div className="relative flex-1 min-w-0">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="search"
                value={projectSearch}
                onChange={(e) => setProjectSearch(e.target.value)}
                placeholder="Rechercher…"
                className="w-full h-10 pl-10 pr-4 rounded-full border border-gray-200 bg-white text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-[#0026E2]"
                aria-label="Rechercher un projet"
              />
            </div>
          ) : (
            <div className="flex-1" />
          )}
        </div>
        <div className="flex-1 lg:hidden" />
        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={() => setIsNotificationModalOpen(true)}
            className="relative flex items-center justify-center hover:opacity-80 transition-opacity"
            title="Notifications"
          >
            <img src={assets.Noti} alt="Notifications" className="w-9 h-9" />
            {unreadCount > 0 ? (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            ) : null}
          </button>

          <div className="relative flex items-center pl-3 border-l border-gray-200" ref={accountMenuRef}>
            <button
              type="button"
              onClick={() => setShowAccountMenu((v) => !v)}
              className="flex items-center gap-9 px-3 py-1.5 rounded-full border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
              title="Compte"
            >
              {/* Logo entreprise (comme NavbarAdminRH) */}
              <div className="h-12 max-w-[360px] flex items-center overflow-visible flex-shrink-0">
                {companyLogoSrc ? (
                  <img
                    src={companyLogoSrc}
                    alt="Logo entreprise"
                    className="h-12 w-auto object-contain scale-[1.5] origin-left"
                  />
                ) : (
                  <span className="text-xs font-semibold text-gray-700">{companyName || displayName}</span>
                )}
              </div>

              {renderProfileAvatar('h-11 w-11')}
            </button>

            {showAccountMenu && (
              <div className="absolute right-0 top-full mt-3 w-[320px] rounded-3xl shadow-xl border border-gray-200 bg-white overflow-hidden z-50">
                <div className="px-4 pt-3 pb-3 relative">
                  <button type="button" onClick={() => setShowAccountMenu(false)} className="absolute right-2 top-2 h-8 w-8 rounded-full flex items-center justify-center hover:bg-black/5" aria-label="Fermer">
                    <FaTimes className="text-gray-600" />
                  </button>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-gray-900">{user?.email || userData?.email}</p>
                    {companyName ? <p className="text-xs text-gray-600">{companyName}</p> : null}
                  </div>
                  <div className="mt-4 flex flex-col items-center">
                    {renderProfileAvatar('h-16 w-16')}
                    <p className="mt-3 text-2xl font-medium text-gray-900">Bonjour {firstName} !</p>
                  </div>
                </div>
                <div className="px-4 pb-4 bg-gray-50 space-y-2">
                  {notif ? (
                    <button
                      type="button"
                      disabled={!notif.action}
                      onClick={() => {
                        if (notif.action) notif.action();
                        setShowAccountMenu(false);
                      }}
                      className="w-full flex items-start gap-3 px-3 py-2.5 rounded-xl text-left text-sm hover:bg-white disabled:opacity-70"
                    >
                      <notif.icon size={18} className="text-[#0026E2] mt-0.5" />
                      <span>
                        <span className="block font-semibold text-gray-800">{notif.label}</span>
                        <span className="block text-xs text-gray-500">{notif.hint}</span>
                      </span>
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      setShowAccountMenu(false);
                      setDialog('devices');
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-gray-800 hover:bg-white"
                  >
                    <Laptop size={18} className="text-gray-500" />
                    Appareils connectés
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAccountMenu(false);
                      setDialog('logout');
                    }}
                    className="w-full flex items-center justify-center gap-2 px-5 py-2 rounded-full border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 font-medium text-sm"
                  >
                    <FaSignOutAlt />
                    Déconnexion
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {dialog === 'devices' ? (
        <LinkedDevicesDialog token={token} onClose={() => setDialog(null)} onRevokedSelf={() => handleLogout()} />
      ) : null}
      {dialog === 'logout' ? (
        <Dialog title="Se déconnecter ?" onClose={() => setDialog(null)}>
          <div className="px-5 pb-5">
            <p className="text-sm text-gray-600">Pour revenir sur cet ordinateur, il faudra scanner à nouveau le QR code avec l’app Secura.</p>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setDialog(null)} className="min-h-[40px] px-4 text-sm font-semibold text-gray-700 hover:bg-gray-100 rounded-lg">Annuler</button>
              <button type="button" onClick={() => handleLogout()} className="min-h-[40px] px-4 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg">Se déconnecter</button>
            </div>
          </div>
        </Dialog>
      ) : null}

      <NotificationModal
        isOpen={isNotificationModalOpen}
        onClose={() => setIsNotificationModalOpen(false)}
      />
    </header>
  );
}
